import { faWeightScale } from "@fortawesome/free-solid-svg-icons";
import { Chart, chartDate, ChartData, CustomTooltipProps, TitleStyle, TooltipContainer, TooltipText } from "./Chart";
import { useMemo } from "react";
import { DateTime, Interval } from "luxon";
import { Guard } from "../utils";
import _ from "lodash";
import { WeightUnit, getWeightKg } from "../quiz/weight";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const CustomTooltip = ({ active, payload }: CustomTooltipProps): JSX.Element => {
  if (active && payload && payload.length) {
    const weight = payload[0].value as number
    return (
      <TooltipContainer>
        <TooltipText>{i18n.t("splash.charts.weightLoss.tooltip", { weight: weight.toPrecision(4) })}</TooltipText>
      </TooltipContainer>
    );
  } else {
    return <></>;
  }
};

type WeightLossChartData = ChartData & {
  bodyWeightLbs: number;
};

const isWeightLossData: Guard<WeightLossChartData> = (data: unknown): data is WeightLossChartData => {
  return (data as WeightLossChartData).bodyWeightLbs !== undefined;
};

type WeightLossChartProps = {
  data: WeightLossChartData[];
  showTitle?: TitleStyle;
  weightUnit: WeightUnit;
  timeToGoal: Interval;
  showOnlyTitle?: boolean;
  showBigHeader?: boolean;
  dedupeMonthTicks?: boolean;
  bendMode?: boolean;
}

export function getWeightLossPredictionData({
  data, showTitle, weightUnit,timeToGoal
} : {
  data: WeightLossChartData[];
  showTitle: TitleStyle | undefined;
  weightUnit: WeightUnit;
  timeToGoal: Interval;
}) {
  if (data != null && data.length > 0) {
    const hideYear = data[data.length - 1].date.year === DateTime.local().year;
    // English "June 30, 2027"; Spanish "30 de junio de 2027".
    const longFormat = i18n.language === "es"
      ? `d 'de' MMMM${hideYear ? '' : " 'de' yyyy"}`
      : `MMMM d${hideYear ? '' : ', yyyy'}`;
    const predictedDate = chartDate(_.last(data)!.date).toFormat(longFormat);
    const predictedDatetoGoal=chartDate(timeToGoal.end).toFormat(longFormat);
    const titlePrefix = TitleStyle.Text === showTitle ? i18n.t("splash.charts.titlePrefix") : i18n.t("splash.charts.weightLoss.iconTitle");
    const lastDataPoint = _.last(data)!.bodyWeightLbs;
    const endWeight = weightUnit === WeightUnit.Pounds ? lastDataPoint : getWeightKg(lastDataPoint, WeightUnit.Pounds);
    const titlePrediction = i18n.t("splash.charts.weightLoss.prediction", { weight: endWeight, unit: weightUnit });
    const titlePredictionDate = timeToGoal.toDuration('weeks').weeks <= 3 ? predictedDatetoGoal : predictedDate;
    
    return {
      titlePrefix,
      titlePrediction,
      titlePredictionDate,
      finalMarker: `${titlePrediction} ${titlePredictionDate}`,
    };
  } 
  return null;
}

const WeightLossChart = (props: WeightLossChartProps) => {
  const { data, showTitle, weightUnit,timeToGoal, showOnlyTitle, showBigHeader, dedupeMonthTicks, bendMode } = props;
  const { t, i18n: { language } } = useTranslation();

  const predictionData = useMemo(() => {
    return getWeightLossPredictionData({ data, showTitle, weightUnit, timeToGoal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showTitle, weightUnit,timeToGoal, language]);

  if (predictionData) {
    const { titlePrefix, titlePrediction, titlePredictionDate } = predictionData;

    return <Chart
      data={data.map((d) => ({ ...d, bodyWeight: weightUnit === WeightUnit.Kilograms ? getWeightKg(d.bodyWeightLbs, WeightUnit.Pounds) : d.bodyWeightLbs }))}
      dataKey="bodyWeight"
      showTitle={showTitle}
      icon={faWeightScale}
      CustomTooltip={CustomTooltip}
      titlePrefix={titlePrefix}
      titlePrediction={titlePrediction}
      titlePredictionDate={titlePredictionDate}
      finalMarker={`${titlePrediction} ${titlePredictionDate}`}
      caret={'▼'}
      markerLocation="bottom"
      yAxisLabel={t("splash.charts.weightLoss.yAxis", { unit: weightUnit })}
      showOnlyTitle={showOnlyTitle}
      showBigHeader={showBigHeader}
      dedupeMonthTicks={dedupeMonthTicks}
      bendMode={bendMode}
    />;
  } else {
    return <></>;
  }
};

export {
  WeightLossChart,
  isWeightLossData,
};
export type { WeightLossChartProps, WeightLossChartData };
